import type { internal } from '@croissants/contracts';
import {
  deliveryNeedsOperator,
  deliveryRiskSettled,
  minutesPastPromisedWindow,
  orderStopped,
} from '@croissants/contracts/projections';
import { type PillState, pillTone } from '@croissants/ui';

/**
 * Lateness risk - the sort order of the admin dashboard (PRD OPS-001, RTE-006).
 *
 * Pure and I/O-free so the ordering an operator relies on at 06:40 is unit
 * tested rather than eyeballed. The server supplies `latenessRiskMinutes`, but a
 * provider ETA that has just moved past the promised window is visible on the row
 * before any recomputation reaches the API, so the client takes the worse of the
 * two. Risk is only ever raised here, never lowered: under-reporting lateness is
 * the failure that costs a breakfast.
 */

export type DashboardRow = internal.DashboardRow;

/**
 * Five bands rather than a number, because the operator scans the board rather
 * than reading it. Each band is rendered with its own label as well as its
 * colour - colour alone never conveys state (PRD NFR-005).
 *
 * `CLOSED` is the fourth count over the board (OPS-001). A cancelled, rejected
 * or refunded order used to band as ON_TRACK, which is the one thing it
 * demonstrably is not: it is finished, it may have cost the guest their
 * morning, and reading it as healthy made the headline figure say nothing was
 * wrong on a day something was.
 *
 * The four live bands, their words and their colours are ONE list, the
 * `latenessBand` family of the pill list (#2004): a band added there is a band
 * here. CLOSED is the fifth, and the one that draws NO pill: the status
 * beside it already says Delivered or Canceled, so the cell stays empty
 * (approved 2026-10-08, P32).
 */
export type LatenessSeverity = PillState<'latenessBand'> | 'CLOSED';

/** The bands that draw a pill: every band but CLOSED. */
export function latenessBandOf(severity: LatenessSeverity): PillState<'latenessBand'> | null {
  return severity === 'CLOSED' ? null : severity;
}

/**
 * Minutes past the promised window at which a row changes band.
 *
 * LATE is not here: it is the row's own `latenessThresholdMinutes`, the setting
 * the lateness alert fires on, so the board and the alert name the same moment
 * (founder, D5 of 2026-10-05). The board used to write its own 10 minutes and
 * show an order the alert had already flagged at 5 as merely at risk.
 */
export const LATENESS_THRESHOLDS = {
  /** ETA still inside the window but with less than this much slack. */
  watchSlackMinutes: 5,
  /** ETA past the end of the window by this much or more. */
  atRiskMinutes: 1,
} as const;

const MINUTE_MS = 60_000;

/**
 * Signed minutes from `earlierIso` to `laterIso`, rounded UP - the one
 * lateness rounding (founder, 14 October 2026), so a positive figure is the
 * server's `minutesPastPromisedWindow` exactly and the slack reads the same way.
 */
function minutesBetween(laterIso: string, earlierIso: string): number | null {
  const later = Date.parse(laterIso);
  const earlier = Date.parse(earlierIso);
  if (Number.isNaN(later) || Number.isNaN(earlier)) return null;
  // `+ 0` reads a slack under a minute as 0, never -0.
  return Math.ceil((later - earlier) / MINUTE_MS) + 0;
}

/**
 * Minutes the order is expected to miss its promised window by. Negative values
 * are slack: -8 means the courier is expected eight minutes before the window
 * closes. Returns the server's own figure when no ETA is available.
 */
export function latenessRiskMinutes(row: DashboardRow, now: Date | null = null): number {
  if (row.estimatedArrival === null) return row.latenessRiskMinutes;
  // An estimate already behind the platform clock is not an arrival any more:
  // the bag is at least as late as now (7KREXP read "210 min late" for 19 days).
  const arrival =
    now !== null && Date.parse(row.estimatedArrival) < now.getTime()
      ? now.toISOString()
      : row.estimatedArrival;
  const computed = minutesBetween(arrival, row.promisedWindowEnd);
  if (computed === null) return row.latenessRiskMinutes;
  /**
   * The server reports 0 both for "no risk" and for "not computed", so a
   * non-positive figure carries no information about slack and the ETA wins. A
   * strictly positive figure is a lateness the server knows about and the client
   * may not - it is never lowered.
   */
  return row.latenessRiskMinutes > 0 ? Math.max(row.latenessRiskMinutes, computed) : computed;
}

/**
 * Which statuses carry no risk (terminal: a delivered order cannot become late)
 * and which are lateness that has already happened (an order that never found a
 * bakery, a courier that never came, a payment that failed) is NOT this file's
 * to decide. Two private `Set`s used to live here, a third classification lived
 * in the worker's `deliverySummary` ladder and a fourth in its `etaRelevant`,
 * and they disagreed: this board sorted an order top as LATE while the delivery
 * panel on its own detail screen read "Created". One table answers all four now
 * (`@croissants/contracts/projections`), and it is exhaustive over `OrderStatus`
 * by construction, so neither side can forget a status the other knows about.
 */

/**
 * The order stopped and no clock is running on it (OPS-001).
 *
 * Two ways for that to be true, and neither is about the delivery: the order
 * was ENDED, or the counter gave it back and nobody else has it. Both used to
 * be members of the delivery-phase table, which is where a flat status put
 * them because it had nowhere else.
 */
function stopped(row: DashboardRow): boolean {
  /*
   * A refusal used to have its own line here - the counter gave it back and
   * nobody else has it yet - and it still has no line. Since #2155 a refused
   * order rests in AWAITING_ASSIGNMENT until the next pass of the every-minute
   * assignment sweep (ALC-004), which re-offers it (a live rung) or cancels it
   * in that pass's own write; neither the resting position nor the rung is
   * stopped, and the cancellation is what `orderStopped` already answers.
   */
  return orderStopped(row.lifecycle.order);
}

/**
 * The row needs an operator NOW, whatever the clock says (OPS-001).
 *
 * Four different things go wrong on four different axes, and the board wants
 * one answer: the cascade found nobody, the card failed, or the delivery came
 * to nothing. A status could hold one of the three at a
 * time, so a payment that failed on an order whose courier had also vanished
 * showed whichever had been written last.
 */
function broken(row: DashboardRow): boolean {
  /*
   * The cascade finding nobody stood first in this list and is gone with the
   * states (decisions 8 and 13): the assignment sweep cancels the order in its
   * own write (ALC-006), and `stopped` above bands a cancelled row CLOSED
   * rather than LATE - which is the right answer, because the rescue an
   * operator owes it is the REFUND, and that is the refunds queue's row rather
   * than this board's.
   */
  if (row.lifecycle.payment === 'FAILED') return true;
  return deliveryNeedsOperator(row.lifecycle.delivery);
}

/**
 * How far past its window's end a DELIVERED order arrived, in milliseconds,
 * or null when it has not arrived (or the instant is not known).
 *
 * The order record's timing table judges the same arrival against the same
 * window end (`orderTimingOf`, LATE on any delta past it), so the board and
 * the record cannot disagree about a delivered order: it used to read "On
 * track" here until it closed while its own record said Late (#2004).
 */
function deliveredPastWindowMs(row: DashboardRow): number | null {
  if (row.lifecycle.delivery !== 'COMPLETED') return null;
  // `?? null`: a worker one release behind sends no `deliveredAt`.
  const deliveredAt = row.deliveredAt ?? null;
  if (deliveredAt === null) return null;
  const arrived = Date.parse(deliveredAt);
  const windowEnd = Date.parse(row.promisedWindowEnd);
  if (Number.isNaN(arrived) || Number.isNaN(windowEnd)) return null;
  return arrived - windowEnd;
}

export function severityOf(row: DashboardRow): LatenessSeverity {
  if (stopped(row)) return 'CLOSED';
  if (broken(row)) return 'LATE';
  // Arrived: no lateness left to compute, only the fact of WHEN - late if it
  // reached the guest after its window closed, as the order record says.
  if (deliveryRiskSettled(row.lifecycle.delivery)) {
    const past = deliveredPastWindowMs(row);
    return past !== null && past > 0 ? 'LATE' : 'ON_TRACK';
  }

  const risk = latenessRiskMinutes(row);
  // The server's flag and the client's fresher ETA, judged on one threshold.
  if (row.latenessFlagged || risk >= row.latenessThresholdMinutes) return 'LATE';
  if (risk >= LATENESS_THRESHOLDS.atRiskMinutes) return 'AT_RISK';
  if (risk >= -LATENESS_THRESHOLDS.watchSlackMinutes) return 'WATCH';
  return 'ON_TRACK';
}

/**
 * The row is in trouble: bad news, whether or not a badge is drawn beside it
 * (founder, 2026-09-06).
 *
 * The order record states its margin as a plain sentence with no band badge
 * over it, so the sentence itself has to carry the signal - and it carries it
 * from the SAME bands the board draws in red and amber, read off the band's
 * colour in the pill list (`latenessBand`, `@croissants/ui` status-pill.tsx)
 * rather than from a second threshold written next to the screen. Two
 * thresholds is how the record and the board would come to disagree about
 * which orders need rescuing, which is the whole family of defect that one
 * colour per band exists to close.
 */
export function latenessIsBadNews(row: DashboardRow): boolean {
  const band = latenessBandOf(severityOf(row));
  if (band === null) return false;
  const tone = pillTone('latenessBand', band);
  return tone === 'danger' || tone === 'warning';
}

/**
 * The minutes figure the risk cell may print BESIDE the badge, or null when no
 * figure is meaningful. A broken status (courier unavailable, delivery failed,
 * unallocated...) is `LATE` by fact, not by ETA arithmetic - its badge comes
 * from the status while any minutes would come from the ETA of a job that no
 * longer exists, which is how a row once read "Late - 17 min spare". A settled
 * row carries no risk, and a live row with no ETA and no server-computed risk
 * has nothing honest to print either.
 */
export function latenessDetailMinutes(row: DashboardRow, now: Date | null = null): number | null {
  // Nothing honest to print: the row has stopped or it is broken. The same
  // questions the badge above asks.
  if (stopped(row) || broken(row)) return null;
  if (deliveryRiskSettled(row.lifecycle.delivery)) {
    // Arrived: how late it arrived, when it did - a measured fact, never an
    // estimate - and nothing when it arrived inside its window.
    const past = deliveredPastWindowMs(row);
    if (past === null || past <= 0) return null;
    return minutesPastPromisedWindow(row.deliveredAt ?? '', row.promisedWindowEnd);
  }
  if (row.estimatedArrival === null && row.latenessRiskMinutes <= 0) return null;
  return latenessRiskMinutes(row, now);
}

const SEVERITY_RANK: Readonly<Record<LatenessSeverity, number>> = {
  LATE: 0,
  AT_RISK: 1,
  WATCH: 2,
  ON_TRACK: 3,
  /* Last: a stopped order is the one row on the board nobody has to reach. */
  CLOSED: 4,
};

/**
 * The admin dashboard's order: worst band first, then by how badly it is missing,
 * then by the window that closes soonest, then by short code so the list is
 * stable between two refreshes of identical data (a list that reshuffles under
 * the operator's finger is worse than a list that is slightly stale).
 */
export function sortByLatenessRisk(rows: readonly DashboardRow[]): DashboardRow[] {
  return [...rows].sort((a, b) => {
    const bandDelta = SEVERITY_RANK[severityOf(a)] - SEVERITY_RANK[severityOf(b)];
    if (bandDelta !== 0) return bandDelta;

    const riskDelta = latenessRiskMinutes(b) - latenessRiskMinutes(a);
    if (riskDelta !== 0) return riskDelta;

    const windowDelta = Date.parse(a.promisedWindowEnd) - Date.parse(b.promisedWindowEnd);
    if (!Number.isNaN(windowDelta) && windowDelta !== 0) return windowDelta;

    return a.shortCode.localeCompare(b.shortCode);
  });
}

export interface LatenessTally {
  readonly late: number;
  readonly atRisk: number;
  readonly watch: number;
  readonly onTrack: number;
  /** Cancelled, rejected or refunded: stopped, and no longer "on track" (OPS-001). */
  readonly closed: number;
  readonly openIncidents: number;
}

/** The header counters. One pass, so the board and the counters cannot disagree. */
export function tally(rows: readonly DashboardRow[]): LatenessTally {
  let late = 0;
  let atRisk = 0;
  let watch = 0;
  let onTrack = 0;
  let closed = 0;
  let openIncidents = 0;
  for (const row of rows) {
    switch (severityOf(row)) {
      case 'LATE':
        late += 1;
        break;
      case 'AT_RISK':
        atRisk += 1;
        break;
      case 'WATCH':
        watch += 1;
        break;
      case 'ON_TRACK':
        onTrack += 1;
        break;
      case 'CLOSED':
        closed += 1;
        break;
    }
    openIncidents += row.openIncidents;
  }
  return { late, atRisk, watch, onTrack, closed, openIncidents };
}
